import { replaceRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import ActionCable from 'actioncable';
import Empty from '../../components/Empty.jsx';
import {
  loadingContent,
  surveyAnalysisDatesContent,
  surveyAnalysisDoesntExistContent,
  surveyAnalysisDoesntExistSurveyContent,
  surveyAnalysisNoResponsesContent,
} from '../../components/EmptyStates.jsx';
import InitiativeHeader from '../../components/headers/PageHeaders/Initiative/InitiativeHeader.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  selectCurrentOrganizationId,
  selectUser,
  selectUserPermissions,
} from '../../modules/auth.js';
import {
  retrieveChangeInsight,
  retrieveChangeInsightDetails,
  selectChangeInsightSettings,
  selectRetrieveChangInsightsState,
  selectSurveyResponsesResults,
} from '../../modules/changeInsights.js';
import {
  retrieveInitiative,
  selectInitiative,
  selectRetrieveInitiativeState,
} from '../../modules/initiatives.js';
import ChangeInsightsAnalysisActionDropdown from '../../components/change-insights/ChangeInsightsAnalysisActionDropdown.jsx';
import {
  analyseSurvey,
  createSurveyAnalysis,
  deleteSurveyAnalysis,
  retrieveSurveyAnalysis,
  selectGenerateSurveyAnalysisState,
  selectRetrieveAnalysisState,
  selectSurveyAnalysis,
  updateSurveyAnalysis,
} from '../../modules/surveyAnalysis.js';
import {
  emptyState,
  getEditorSaveDataPromise,
  getReadyStatusPromise,
  validateEditorData,
} from '../../components/editor/defaultOptions.js';
import { PlusButtonIcon } from '../../components/icons/PlusButtonIcon.jsx';
import ChangeInsightsAnalysisDetails from '../../components/change-insights/ChangeInsightsAnalysisDetails.jsx';
import AuthorizedAction, {
  isAuthorized,
} from '../../components/AuthorizedAction.js';
import { updateNotificationState } from '../../modules/notifications.js';
import ChangeInsightsOverview from '../../components/change-insights/ChangeInsightsOverview.jsx';

const mapStateToProps = (state, ownProps) => {
  const initiative = selectInitiative(
    ownProps.initiativeId,
    ownProps.initiativeType
  )(state);

  return {
    initiative,
    retrieveInitiativeSuccess: selectRetrieveInitiativeState(state).success,
    retrieveAnalysisSuccess: selectRetrieveAnalysisState(state).success,
    retrieveChangInsightsSuccess:
      selectRetrieveChangInsightsState(state).success,
    analysisGenerationState: selectGenerateSurveyAnalysisState(state),
    user: selectUser(state),
    surveyAnalysis: selectSurveyAnalysis(state),
    apiUrl: state.config.apiUrl,
    organizationId: selectCurrentOrganizationId(state),
    surveyResponses: selectSurveyResponsesResults(state),
    changeInsightSettings: selectChangeInsightSettings(
      initiative ? initiative.surveyId : null
    )(state),
    permissions: selectUserPermissions(state),
  };
};

const mapDispatchToProps = {
  retrieveInitiative,
  retrieveChangeInsight,
  retrieveChangeInsightDetails,
  analyseSurvey,
  retrieveSurveyAnalysis,
  createSurveyAnalysis,
  updateSurveyAnalysis,
  deleteSurveyAnalysis,
  updateNotificationState,
};

const ChangeInsightsAnalysisContainer = (props) => {
  const {
    retrieveInitiativeSuccess,
    retrieveAnalysisSuccess,
    retrieveChangInsightsSuccess,
    analysisGenerationState,
    updateNotificationState,
    initiative,
    retrieveChangeInsightDetails,
    initiativeId,
    initiativeType,
    analyseSurvey,
    retrieveSurveyAnalysis,
    createSurveyAnalysis,
    updateSurveyAnalysis,
    deleteSurveyAnalysis,
    retrieveInitiative,
    surveyResponses,
    changeInsightSettings,
    permissions,
    surveyAnalysis,
    apiUrl,
    user,
  } = props;
  const [cable, setCable] = useState(null);
  const [changeInsightId, setChangeInsightId] = useState(null);
  const [questionsSetting, setQuestionSetting] = useState([]);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [fullText, setFullText] = useState('');
  const [messages, setMessages] = useState([]);
  const [editorData, setEditorData] = useState(null);
  const [readyToEdit, setReadyToEdit] = useState(false);
  const [readyToApply, setReadyToApply] = useState(false);
  const [editorInstance, setEditorInstance] = useState(null);
  const [isFirstGeneration, setIsFirstGeneration] = useState(true);
  const [received, setReceived] = useState(false);
  const [showStream, setShowStream] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  const [loading, setLoading] = useState(false);

  const isAiAnalysisExist = surveyAnalysis?.aiResults;
  const showEmptyState =
    !(
      showStream ||
      fullText ||
      !isFirstGeneration ||
      surveyAnalysis ||
      isAiAnalysisExist
    ) && retrieveAnalysisSuccess;

  const showEndDateEmptyState = () => {
    return (
      changeInsightSettings &&
      new Date(changeInsightSettings.endDate) > new Date()
    );
  };

  const notifyLimitError = () => {
    updateNotificationState({
      isShown: true,
      type: 'danger',
      forPath: '/change-insights/analysis/show',
      message:
        'Your data input exceeds the allowed limit. Please contact us for assistance: info@changesync.com.',
    });
  };

  const fixOrder = () => {
    const sortedMsg = messages.sort(
      (a, b) => a.sequence_number - b.sequence_number
    );
    let sortedText = '';
    sortedMsg.map((msg) => (sortedText += msg.content));

    setFullText(sortedText);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Use wss:// for https (production), ws:// for http (development)
      const wsProtocol = apiUrl.startsWith('https://') ? 'wss://' : 'ws://';
      const cable = ActionCable.createConsumer(
        `${wsProtocol}${apiUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')}/cable`
      );

      setCable(cable);

      window.addEventListener('offline', () => setHasError(true));
    }

    retrieveInitiative(initiativeType, initiativeId);
  }, []);

  const onError = () => {
    setShowStream(false);
    setHasError(true);
  };

  const handleAnalyzeSurvey = () => {
    if (initiative.project.archived)
      return window.alert(getOrgTermsContent('archivedProjectMessage'));
    setShowNotification(false);
    setReceived(false);
    setShowStream(true);
    setFullText('');
    setMessages([]);
    setReadyToEdit(false);
    setEditorData(null);
    analyseSurvey(changeInsightId, { additionalPrompt });
  };

  const handleFinishInput = () => {
    const sentences = fullText.split('\n');

    const blocks = [];

    let listStarted = false;
    let isUnordered = false;
    let tempListItems = [];

    sentences.map((sentence) => {
      const listMarker = sentence.includes('***');

      if (listStarted && !listMarker) {
        isUnordered = isNaN(sentence[0]);
        const formattedListItem = sentence.replace(/^\d+\.?\s*|\s*-/g, '');
        tempListItems.push(formattedListItem && formattedListItem);
      }

      if (listMarker && listStarted) {
        blocks.push({
          id: String(Math.random().toString(36).slice(2, 7)),
          type: 'list',
          data: {
            style: !isUnordered ? 'ordered' : 'unordered',
            items: tempListItems,
          },
        });

        tempListItems = [];
      }

      if (listMarker) {
        listStarted = !listStarted;
      }

      if (!listMarker && !listStarted) {
        const isBold = sentence.includes('**');
        const cleanedSentence = isBold
          ? sentence.replace(/\*\*/g, '')
          : sentence;

        blocks.push({
          id: String(Math.random().toString(36).slice(2, 7)),
          type: isBold ? 'header' : 'paragraph',
          data: {
            text: cleanedSentence,
          },
        });
      }
    });
    setReadyToEdit(true);
    setEditorData({ ...emptyState(''), blocks });
  };

  const handleSaveResults = () => {
    setLoading(true);
    getReadyStatusPromise(editorInstance).then(() =>
      getEditorSaveDataPromise(editorInstance).then((updatedData) => {
        const data = {
          ai_results: JSON.stringify(updatedData),
        };

        const promise = surveyAnalysis
          ? updateSurveyAnalysis(changeInsightId, data)
          : createSurveyAnalysis(changeInsightId, data);

        return promise.then(() => window.location.reload());
      })
    );
  };

  const handleDeleteAnalysis = () => {
    if (initiative.project.archived)
      return window.alert(getOrgTermsContent('archivedProjectMessage'));
    if (
      !window.confirm('Are you sure you want to detach this AI Analysis form?')
    )
      return;

    deleteSurveyAnalysis(changeInsightId).then(() => window.location.reload());
  };

  const handleEditAnalysis = () => {
    if (initiative.project.archived)
      return window.alert(getOrgTermsContent('archivedProjectMessage'));
    setReadyToEdit(true);
  };

  useEffect(() => {
    if (initiative?.surveyId) {
      setChangeInsightId(initiative.surveyId);
    }
  }, [initiative]);

  useEffect(() => {
    if (changeInsightId) {
      retrieveChangeInsightDetails(changeInsightId);
      retrieveSurveyAnalysis(changeInsightId);
    }

    if (cable && changeInsightId) {
      const subscription = cable.subscriptions.create(
        {
          channel: 'SurveyAnalysisChannel',
          survey_id: changeInsightId,
          user_id: user.id,
        },
        {
          received(data) {
            if (!('last_content' in data)) {
              setMessages((prevMessages) => [...prevMessages, data]);
            } else {
              setReceived(true);
              setReadyToApply(true);
              isFirstGeneration && setIsFirstGeneration(false);
              window.scrollTo(0, document.body.scrollHeight);
            }
          },
        }
      );
      subscription.consumer.connection.webSocket.onerror = onError;
      return () => subscription.unsubscribe();
    }
  }, [changeInsightId, cable]);

  useEffect(() => {
    if (isAiAnalysisExist && !showStream) {
      setEditorData(validateEditorData(surveyAnalysis.aiResults));
    }
  }, [surveyAnalysis]);

  useEffect(() => {
    !showStream && messages?.length && setHasError(true);
    showStream && window.scrollTo(0, document.body.scrollHeight);

    messages?.length &&
      setFullText((prevText) =>
        showStream ? prevText + messages[messages.length - 1].content : ''
      );
  }, [messages]);

  useEffect(() => {
    received && fixOrder();
  }, [received]);

  useEffect(() => {
    if (
      analysisGenerationState &&
      analysisGenerationState.error === 'limit_error'
    ) {
      notifyLimitError();
      setReceived(false);
      setShowStream(false);
      setFullText('');
      setMessages([]);
      setShowNotification(true);
    }
  }, [analysisGenerationState]);

  if (!retrieveInitiativeSuccess && !retrieveAnalysisSuccess)
    return <Empty simplified content={loadingContent} />;

  if (!initiative.surveyId) {
    return (
      <React.Fragment>
        <InitiativeHeader initiative={initiative} hideSubHeader />
        <Empty
          content={surveyAnalysisDoesntExistSurveyContent}
          action={
            isAuthorized('update', `${initiativeType}Initiative`, permissions)
              ? () =>
                  replaceRoute('change-insights/studio/show', {
                    initiativeType,
                    initiativeId,
                  })
              : null
          }
          addBtn
        />
      </React.Fragment>
    );
  }
  if (retrieveChangInsightsSuccess && showEndDateEmptyState()) {
    return (
      <React.Fragment>
        <InitiativeHeader initiative={initiative} hideSubHeader />
        <Empty content={surveyAnalysisDatesContent} />
      </React.Fragment>
    );
  }

  if (retrieveChangInsightsSuccess && !surveyResponses.length) {
    return (
      <React.Fragment>
        <InitiativeHeader initiative={initiative} hideSubHeader />
        <Empty content={surveyAnalysisNoResponsesContent} />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <InitiativeHeader initiative={initiative} hideSubHeader />
      <SubHeader small>
        <div className="flex flex-justify">
          <h2 className="horizontal-label">ChangeInsights Analysis</h2>
          {isAiAnalysisExist &&
          isAuthorized('update', 'survey', permissions) &&
          !hasError ? (
            <AuthorizedAction
              action="update"
              subject={`${initiativeType}Initiative`}
            >
              <ChangeInsightsAnalysisActionDropdown
                changeInsightSettings={changeInsightSettings}
                questionsSetting={questionsSetting}
                setQuestionSetting={setQuestionSetting}
                onRegenerate={handleAnalyzeSurvey}
                onDeleteAnalysis={() => handleDeleteAnalysis(changeInsightId)}
                onEdit={handleEditAnalysis}
                hasError={hasError}
              />
            </AuthorizedAction>
          ) : null}

          {!isAiAnalysisExist &&
          !editorData &&
          !fullText &&
          !hasError &&
          initiative.surveyId &&
          surveyResponses.length &&
          !showEndDateEmptyState() &&
          isAuthorized('update', 'survey', permissions) ? (
            <AuthorizedAction
              action="update"
              subject={`${initiativeType}Initiative`}
            >
              <Button onClick={handleAnalyzeSurvey} bsStyle="success" addBtn>
                <PlusButtonIcon />
                Add AI Analysis
              </Button>
            </AuthorizedAction>
          ) : null}
        </div>
      </SubHeader>
      <ChangeInsightsOverview
        initiative={initiative}
        survey={changeInsightSettings}
        surveyResponses={surveyResponses}
      />
      <SubHeader medium>
        <h2 className="flex">Feedback Form Analysis</h2>
      </SubHeader>

      {hasError ? (
        <div className="component-xl component-bg-white component-border-bottom text-center">
          <h3 className="text-danger">
            {' '}
            Oops! It looks like we've encountered an issue with the OpenAI API.
          </h3>
          <br />
          <br />
          <p className="no-margin">
            We're unable to process your request at the moment. Please try again
            later.
            <br />
            If the problem persists, contact our support team for assistance.
          </p>
        </div>
      ) : null}

      {showEmptyState &&
      initiative.surveyId &&
      surveyResponses.length &&
      !showEndDateEmptyState() ? (
        <div className="component component-bg-white">
          <Empty
            content={surveyAnalysisDoesntExistContent}
            action={!hasError ? handleAnalyzeSurvey : null}
            actionAuthorization={{
              action: 'update',
              subject: `${initiativeType}Initiative`,
            }}
          />
        </div>
      ) : showStream && !messages.length && !hasError ? (
        <Empty simplified content={loadingContent} />
      ) : (
        <ChangeInsightsAnalysisDetails
          readyToEdit={readyToEdit}
          setReadyToEdit={setReadyToEdit}
          fullText={fullText}
          readyToApply={readyToApply}
          setReadyToApply={setReadyToApply}
          isFirstGeneration={isFirstGeneration}
          additionalPrompt={additionalPrompt}
          setAdditionalPrompt={setAdditionalPrompt}
          handleFinishInput={handleFinishInput}
          handleAnalyzeSurvey={handleAnalyzeSurvey}
          handleSaveResults={handleSaveResults}
          isAiAnalysisExist={isAiAnalysisExist}
          editorData={editorData}
          setEditorData={setEditorData}
          setEditorInstance={setEditorInstance}
          loading={loading}
          received={received}
          hasError={hasError}
        />
      )}
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeInsightsAnalysisContainer);
